import { useLatestRef } from '@magicschool/spellbook/hooks/useLatestRef';
import { useEffect, useState } from 'react';

function typeDelay(char: string, charIndex: number, totalLength: number) {
  const base = 42;
  if (charIndex === 0) return 320 + Math.random() * 80;
  const startFactor = charIndex < 3 ? 1.6 - charIndex * 0.2 : 1;
  const punctFactor = /[.,!?;:\s]/.test(char) ? 1.4 : 1;
  const endFactor = charIndex > totalLength - 4 ? 1.2 : 1;
  const jitter = (Math.random() - 0.5) * 20;
  return Math.round(base * startFactor * punctFactor * endFactor + jitter);
}

function holdDuration(text: string) {
  const words = text.split(' ').length;
  const readMs = (words / 160) * 60_000;
  return Math.max(2200, Math.min(5000, readMs));
}

export function useTypingAnimation(
  active: boolean,
  prompts: string[],
  onAnimatedPlaceholderChange?: (text: string, index: number) => void,
) {
  const [displayText, setDisplayText] = useState('');
  const promptsRef = useLatestRef(prompts);
  const onChangeRef = useLatestRef(onAnimatedPlaceholderChange);

  useEffect(() => {
    if (!active) {
      setDisplayText('');
      return;
    }

    let cancelled = false;

    function after(delay: number, fn: () => void) {
      setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
    }

    function runPrompt(promptIndex: number) {
      const all = promptsRef.current;
      if (!all.length) return;
      const idx = promptIndex % all.length;
      const prompt = all[idx] ?? '';
      onChangeRef.current?.(prompt, idx);

      function typeChar(i: number) {
        setDisplayText(prompt.slice(0, i));
        if (i < prompt.length) {
          after(typeDelay(prompt[i] ?? '', i, prompt.length), () => typeChar(i + 1));
        } else {
          after(holdDuration(prompt), () => deleteChar(prompt.length));
        }
      }

      function deleteChar(i: number) {
        setDisplayText(prompt.slice(0, i));
        if (i > 0) {
          after(32, () => deleteChar(i - 1));
        } else {
          after(480, () => runPrompt(promptIndex + 1));
        }
      }

      typeChar(0);
    }

    runPrompt(0);
    return () => {
      cancelled = true;
    };
  }, [active, promptsRef, onChangeRef]);

  return displayText;
}
